import * as Lambda from '../../services/LambdaApi';
import { Document, ServiceDocumentSource } from '../types/Document';
import { DocumentListFilters } from '../types/DocumentFilters';
import { buildListDocsParams, normalizeDocIds } from '../utils/document';
import { uploadToSignedUrl } from '../../services/imageUpload';
import { formatFileNameWithTimestamp } from '../../utils/imageUpload';
import { paginationWrapper } from '../../utils/paginationWrapper';

const fetchCommunityDocs = async (
    facilityId: string,
    pageNo: number = 1,
    pageSize: number = 100,
    listFilters?: DocumentListFilters,
) => {
    const apiPath = `/s2l/${facilityId}/listDocs`;
    const params = buildListDocsParams({
        sendCommunityDocs: true,
        includeAddedByName: true,
    }, pageNo, pageSize, listFilters);

    const response = await Lambda.Post(apiPath, params, 'lambda');
    return response;
};

const fetchMyDocs = async (
    facilityId: string,
    residentId: string,
    pageNo: number = 1,
    pageSize: number = 100,
    listFilters?: DocumentListFilters,
) => {
    const apiPath = `/s2l/${facilityId}/listDocs`;
    const params = buildListDocsParams({
        residentId: residentId,
        includeAddedByName: true,
    }, pageNo, pageSize, listFilters);

    const response = await Lambda.Post(apiPath, params, 'lambda');
    return response;
};

const fetchResidentGroupDocs = async (
    facilityId: string,
    residentId: string,
    pageNo: number = 1,
    pageSize: number = 100,
    listFilters?: DocumentListFilters,
) => {
    const apiPath = `/s2l/${facilityId}/listDocs`;
    const params = buildListDocsParams({
        sendGroupDocs: true,
        includeAddedByName: true,
        residentId: residentId,
    }, pageNo, pageSize, listFilters);

    const response = await Lambda.Post(apiPath, params, 'lambda');
    return response;
};

const fetchByDocIds = async (facilityId: string, docIds: string[]) => {
    try {
        const apiPath = `/s2l/${facilityId}/listDocs`;
        const params =
        {
            Filter: {
                _id: {
                    $in: docIds
                },
                includeAddedByName: true
            },
            pagingSorting: {
                sort: [{
                    DateAdded: "asc"
                }]
            }
        };
        const response = await Lambda.Post(apiPath, params, 'lambda');
        if (!response || !response.Result || !response.Result.Content || !response.Result.Success) {
            throw new Error("Error fetching facility details");
        }
        return response.Result.Content as { Result: Document[]; TotRecords: number; };
    } catch (err) {
        throw new Error("Error fetching document details");
    }
};

const uploadGroupDocument = async (facilityId: string, payload: { docId: string; Name: string }) => {
    const apiPath = `/s2l/${facilityId}/uploadGroupDocument`;
    const response = await Lambda.Post(apiPath, payload, 'lambda');
    if (!response?.Result?.Success) {
        throw new Error(response?.Result?.Error || "Error getting upload URL");
    }
    return response.Result?.Content;
};

const updateGroupDocument = async (facilityId: string, payload: { _id: string; Name: string; Access: string[]; files: string[]; UpdatedByResidentId?: string }) => {
    const apiPath = `/s2l/${facilityId}/updateGroupDocument`;
    const response = await Lambda.Post(apiPath, payload, 'lambda');
    if (!response?.Result?.Success) {
        throw new Error(response?.Result?.Error || "Error updating group document");
    }
    return response.Result?.Content;
};

// groupId omitted => a personal "My Docs" upload, private to the resident and not linked to any group.
// groupId present => visible only to that group's members (via Group.DocIds/ResidentIds), not the whole community.
const addDocumentToGroup = async (facilityId: string, UploadedByResidentId: string, documentData: { Name: string; Description?: string; file: File; groupId?: string }) => {
    try {
        if (!facilityId) throw new Error('Facility ID is required');
        if (!documentData?.file) throw new Error('File is required');
        if (!documentData?.Name) throw new Error('Document name is required');
        if (!UploadedByResidentId) throw new Error('Resident ID is required');

        const formattedFileName = formatFileNameWithTimestamp(documentData.file.name);
        const Access = documentData.groupId ? [documentData.groupId] : [UploadedByResidentId];

        const addApiPath = `/s2l/${facilityId}/addDocumentToGroup`;
        const addParams = {
            Facility: facilityId,
            UploadedByResidentId,
            Name: documentData.Name,
            Description: documentData.Description || '',
            files: [formattedFileName],
            Access,
        };

        const addResponse = await Lambda.Post(addApiPath, addParams, 'lambda');
        if (!addResponse?.Result?.Success) {
            throw new Error(addResponse?.Result?.Error || "Error adding document");
        }

        if (!addResponse.Result?.Content || typeof addResponse.Result?.Content !== 'string') {
            throw new Error("Error adding document");
        }

        const presignedUrl = await uploadGroupDocument(facilityId, {
            docId: addResponse.Result?.Content,
            Name: formattedFileName,
        });

        const uploadResponse = await uploadToSignedUrl(documentData.file, presignedUrl);
        if (!uploadResponse.ok) {
            throw new Error("Failed to upload file");
        }

        await updateGroupDocument(facilityId, {
            _id: addResponse.Result?.Content,
            Name: documentData.Name,
            Access,
            files: [formattedFileName],
            UpdatedByResidentId: UploadedByResidentId,
        });

        return { docId: addResponse.Result?.Content, fileName: formattedFileName };
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : "Error adding document");
    }
};

const deleteDocumentFromGroup = async (facilityId: string, docId: string, UploadedByResidentId: string) => {
    try {
        if (!facilityId) throw new Error('Facility ID is required');
        if (!docId) throw new Error('Document ID is required');
        if (!UploadedByResidentId) throw new Error('Resident ID is required');

        const apiPath = `/s2l/${facilityId}/deleteDocumentFromGroup`;
        const params = {
            _id: docId,
            UploadedByResidentId,
            Facility: facilityId,
        };

        const response = await Lambda.Post(apiPath, params, 'lambda');
        if (!response?.Result?.Success) {
            throw new Error(response?.Result?.Error || "Error deleting document");
        }

        return response.Result;
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : "Error deleting document");
    }
};

// Resolves a service/asset's DocIds into full Document records, preserving DocIds order.
const resolveServiceDocuments = async (
    facilityId: string,
    source: ServiceDocumentSource
): Promise<Document[]> => {
    const normalizedDocIds = normalizeDocIds(source?.DocIds);
    if (!facilityId || normalizedDocIds.length === 0) {
        return [];
    }

    const { Result } = await fetchByDocIds(facilityId, normalizedDocIds);
    const documents = Array.isArray(Result) ? Result : [];
    const docsById = new Map(
        documents
            .filter((doc): doc is Document => Boolean(doc?._id))
            .map((doc) => [String(doc._id), doc])
    );

    return normalizedDocIds
        .map((docId) => docsById.get(docId))
        .filter((doc): doc is Document => Boolean(doc));
};

// Paginates through every doc for one listDocs "source" filter (community/my/group),
// unfiltered by search/service/asset, to derive which services actually have documents
// attached — used to build the "Filter by Service" options rather than to display docs.
const listAllDocsForSource = async (facilityId: string, sourceFilter: Record<string, unknown>): Promise<Document[]> => {
    return paginationWrapper({
        apiCall: async ({ pagingSorting }: { pagingSorting: { page_size: number; page_no: number } }) => {
            const apiPath = `/s2l/${facilityId}/listDocs`;
            const params = {
                Filter: sourceFilter,
                includeConnectedEntities: true,
                pagingSorting: { ...pagingSorting, sort: [{ DateAdded: 'asc' }] },
            };
            const response = await Lambda.Post(apiPath, params, 'lambda');
            const content = response?.Result?.Success ? response.Result.Content : null;
            return {
                Result: Array.isArray(content?.Result) ? content.Result : [],
                TotRecords: typeof content?.TotRecords === 'number' ? content.TotRecords : 0,
            };
        },
        params: {},
    }) as Promise<Document[]>;
};

// Collects the distinct set of services/assets actually attached to at least one document
// visible to this resident (community, group, and personal docs combined) — the "Filter by
// Service/Asset" options should only ever offer these, not every service or asset on file.
// Groups are intentionally NOT filtered this way: a resident should see every group they're
// a member of (sidebar nav, "all" tab sections, "Filter by Group") regardless of whether it
// has a document yet — only services/assets get pared down to "has a document".
const fetchDocumentFilterOptions = async (
    facilityId: string,
    residentId: string,
): Promise<{
    services: Array<{ _id: string; name?: string }>;
    assets: Array<{ _id: string; name?: string }>;
}> => {
    const [communityDocs, myDocs, groupDocs] = await Promise.all([
        listAllDocsForSource(facilityId, { sendCommunityDocs: true }),
        listAllDocsForSource(facilityId, { residentId }),
        listAllDocsForSource(facilityId, { sendGroupDocs: true, residentId }),
    ]);

    const servicesById = new Map<string, { _id: string; name?: string }>();
    const assetsById = new Map<string, { _id: string; name?: string }>();

    // A given service/asset can show up on multiple docs, and not every occurrence
    // carries a name — upsert rather than "first write wins" so a later, named
    // occurrence still fills in a name a first, nameless occurrence left blank.
    [...communityDocs, ...myDocs, ...groupDocs].forEach((doc) => {
        doc.connectedEntities?.services?.forEach((service) => {
            if (!service?._id) return;
            const existing = servicesById.get(service._id);
            if (!existing || (!existing.name && service.name)) {
                servicesById.set(service._id, { _id: service._id, name: service.name });
            }
        });
        doc.connectedEntities?.assets?.forEach((asset) => {
            if (!asset?._id) return;
            const existing = assetsById.get(asset._id);
            if (!existing || (!existing.name && asset.AssetName)) {
                assetsById.set(asset._id, { _id: asset._id, name: asset.AssetName });
            }
        });
    });

    return {
        services: Array.from(servicesById.values()),
        assets: Array.from(assetsById.values()),
    };
};

export {
    fetchCommunityDocs,
    fetchMyDocs,
    fetchResidentGroupDocs,
    fetchByDocIds,
    addDocumentToGroup,
    deleteDocumentFromGroup,
    resolveServiceDocuments,
    fetchDocumentFilterOptions,
};
